import { watch, toRaw, ref } from 'vue';
import { defineStore } from 'pinia';

export type TModes = 'EXISTING' | 'NEW' | 'INITIAL';
export const MODE: Record<TModes, TModes> = {
    EXISTING: 'EXISTING',
    NEW: 'NEW',
    INITIAL: 'INITIAL',
};

export interface IApplicationStateType {
    mode: TModes;
    first_load: boolean;
    page_loaded: boolean;
    request_id: boolean | string;
    is24HourFormat: boolean;
    business_id: boolean | string;
    location_id: boolean | string;
    loading: boolean;
    noEnquiryOrBooking: boolean;
    isExperiment: boolean;
}

export const useStateStore = defineStore('mode', () => {
    const defaultState = () => {
        return {
            mode: MODE.INITIAL,
            first_load: true,
            request_id: false,
            business_id: false,
            location_id: false,
            page_loaded: false,
            is24HourFormat: false,
            loading: false,
            noEnquiryOrBooking: false,
            isExperiment: false,
        };
    };
    const state = ref<IApplicationStateType>(defaultState());
    watch(
        state,
        (newvalue) => {
            localStorage.setItem(
                'appStateStore',
                JSON.stringify(toRaw(newvalue))
            );
        },
        { deep: true }
    );

    const getLocalStorage = () => {
        const appStateStore = localStorage.getItem('appStateStore');

        if (!appStateStore) {
            return null;
        }

        return JSON.parse(appStateStore);
    };
    const clearLocalStorage = () => {
        localStorage.removeItem('appStateStore');
    };
    const getLocalStorageItem = (key: string) => {
        const appStateStore = localStorage.getItem('appStateStore');

        if (!appStateStore) {
            return null;
        }
        return JSON.parse(appStateStore)[key];
    };

    const restoreFromLocalStorage = () => {
        const localStorageState = getLocalStorage();
        if (localStorageState) {
            state.value = { ...localStorageState };
            return true;
        }
        return false;
    };
    const startLoading = (): void => {
        state.value.loading = true;
    };
    const endLoading = (): void => {
        state.value.loading = false;
    };

    const toggleTimeFormat = () => {
        state.value.is24HourFormat = !state.value.is24HourFormat;
    };
    const setFirstLoadFalse = (): void => {
        state.value.first_load = false;
    };
    const isFirstLoad = (): boolean => {
        return state.value.first_load === false;
    };
    const getRequestId = (request_id?: any): any => {
        if (request_id) {
            setRequestId(request_id);
            return request_id;
        }

        request_id = getLocalStorageItem('request_id');

        if (request_id) {
            setRequestId(request_id);
            return request_id;
        }

        return state.value.request_id;
    };
    const setRequestId = (request_id: string | boolean): void => {
        state.value.request_id = request_id;
    };
    const getBusiness = (business_id?: any): any => {
        if (business_id) {
            setBusiness(business_id);
            return business_id;
        }

        business_id = getLocalStorageItem('business_id');

        if (business_id) {
            setBusiness(business_id);
            return business_id;
        }

        return state.value.business_id;
    };
    const setBusiness = (business_id: string | boolean): void => {
        state.value.business_id = business_id;
    };
    const isModeNew = (mode: TModes): boolean => {
        return mode === MODE.NEW;
    };
    const isModeExisting = (mode: TModes): boolean => {
        return mode === MODE.EXISTING;
    };
    const getMode = (): TModes => {
        let mode = getLocalStorageItem('mode');
        if (mode) {
            state.value.mode = mode;
        }

        return state.value.mode;
    };
    const setMode = (mode: TModes): void => {
        state.value.mode = mode;
    };

    const setLocationId = (location_id: string | boolean): void => {
        state.value.location_id = location_id;
    };

    const setPageLoadedTrue = (): void => {
        state.value.page_loaded = true;
    };
    const isPageLoaded = (): boolean => {
        return state.value.page_loaded === true;
    };

    const getLocationId = (location_id?: any): any => {
        if (location_id !== undefined && location_id !== null) {
            setLocationId(location_id);
        } else if (getLocalStorageItem('location_id') !== null) {
            setLocationId(getLocalStorageItem('location_id'));
        }

        return state.value.location_id;
    };

    const setNoEnquiry = () => {
        state.value.noEnquiryOrBooking = true;
    };

    const withNoEnquiry = () => {
        return state.value.noEnquiryOrBooking;
    };

    const resetState = () => {
        state.value = defaultState();
    };

    const setExperimentTrue = () => {
        state.value.isExperiment = true;
    };

    return {
        state,
        startLoading,
        endLoading,
        toggleTimeFormat,
        setFirstLoadFalse,
        isFirstLoad,
        getLocalStorageItem,
        getRequestId,
        setRequestId,
        getLocationId,
        setLocationId,
        getBusiness,
        setBusiness,
        isModeNew,
        isModeExisting,
        getMode,
        setMode,
        setNoEnquiry,
        withNoEnquiry,
        resetState,
        isPageLoaded,
        setPageLoadedTrue,
        restoreFromLocalStorage,
        setExperimentTrue,
        clearLocalStorage
    };
});
